import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { billingApi } from '../api/billing';
import { useAuth } from './AuthContext';
import { getCurrencySymbol } from '../utils/currency';

/**
 * Plan Context — manages dynamic plan catalog from backend.
 * Product Owner can change limits/pricing anytime — this context
 * fetches fresh data and provides it to all components.
 */

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [plansData, setPlansData] = useState(null);
  const [plansMap, setPlansMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  /**
   * Fetch plan catalog from backend.
   * Called on mount and can be refreshed anytime.
   */
  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.getPlans();

      // Build a map for quick lookups: { free: {...}, growth: {...}, pro: {...} }
      const map = {};
      data.plans?.forEach(plan => {
        map[plan.planCode] = plan;
      });

      setPlansData(data);
      setPlansMap(map);
    } catch (err) {
      console.error('Failed to fetch plan catalog:', err);
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when user logs in (not on public pages)
  useEffect(() => {
    if (isLoggedIn && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchPlans();
    }
    if (!isLoggedIn) {
      // Reset when user logs out so we fetch fresh data on next login
      fetchedRef.current = false;
      setPlansData(null);
      setPlansMap({});
      setLoading(false);
      setError(null);
    }
  }, [isLoggedIn, fetchPlans]);

  /**
   * Get a specific plan by code.
   */
  const getPlan = useCallback((planCode) => {
    return plansMap[planCode] || null;
  }, [plansMap]);

  /**
   * Get pricing for a specific plan and duration.
   */
  const getPricing = useCallback((planCode, durationMonths = 1) => {
    const plan = plansMap[planCode];
    if (!plan?.pricingTiers) return null;

    return plan.pricingTiers.find(t => t.durationMonths === durationMonths) || null;
  }, [plansMap]);

  /**
   * Get all pricing tiers for a plan.
   */
  const getPricingTiers = useCallback((planCode) => {
    const plan = plansMap[planCode];
    return plan?.pricingTiers || [];
  }, [plansMap]);

  /**
   * Get limits for a plan.
   * Returns: { flocks, eggRecords, sales, expenses, incubatorBatches }
   * Values: -1 = unlimited, positive = limit, 0 = no access
   */
  const getLimits = useCallback((planCode) => {
    const plan = plansMap[planCode];
    return plan?.limits || {
      flocks: 1,
      eggRecords: 20,
      sales: 10,
      expenses: 10,
      incubatorBatches: 1,
    };
  }, [plansMap]);

  /**
   * Check if a feature is enabled for a plan.
   */
  const hasFeature = useCallback((planCode, featureName) => {
    const plan = plansMap[planCode];
    return plan?.features?.[featureName] === true;
  }, [plansMap]);

  /**
   * Format price for display.
   */
  const formatPrice = useCallback((planCode, durationMonths = 1) => {
    const pricing = getPricing(planCode, durationMonths);
    const currency = plansData?.currency || 'USD';

    if (!pricing) return '';

    const symbol = getCurrencySymbol(currency);
    const price = pricing.pricePerMonth;

    if (currency === 'IDR' || currency === 'PHP') {
      return `${symbol} ${price.toLocaleString()}`;
    }
    return `${symbol}${price.toLocaleString()}`;
  }, [getPricing, plansData?.currency]);

  const value = {
    plans: plansData?.plans || [],
    countryCode: plansData?.countryCode || 'OTHER',
    currency: plansData?.currency || 'USD',
    loading,
    error,
    refresh: fetchPlans,
    getPlan,
    getPricing,
    getPricingTiers,
    getLimits,
    hasFeature,
    formatPrice,
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
}

/**
 * Hook to use plan context.
 */
export function usePlans() {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlans must be used within a PlanProvider');
  }
  return context;
}

// getCurrencySymbol is imported from utils/currency.js
